import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

// Single-line badge row with overflow: renders as many FULL-SIZE badges as fit
// on one line, plus a `+N` toggle that reveals the rest as a wrapped list below.
//
// The visible count is measured responsively (ResizeObserver + a fit loop) so
// badges stay legible — never squeezed/ellipsised to share the row — and the
// row plus the `+N` toggle always stay inside the card. This is the behavior
// NVIDIA's Kaizen OverflowGroup gives for free; this hand-rolled version is the
// seam we'll swap once on React 18 (DPX-902).
//
// Renders the app's existing `.badge badge--{category}` markup (styled by
// partials/_badges.scss, including dark mode) rather than a UI-library badge.

export default function BadgeOverflowGroup({ badges = [] }) {
  const rowRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(badges.length);
  const [rowWidth, setRowWidth] = useState(null);

  // When the row width or the badge set changes, start from "show everything"
  // and let the fit loop below shrink the count back down.
  useLayoutEffect(() => {
    setVisibleCount(badges.length);
  }, [rowWidth, badges]);

  // Shrink the inline count one badge at a time until the single-line row stops
  // overflowing. Runs before paint, so the user never sees the overflowing state.
  useLayoutEffect(() => {
    if (expanded) {
      return;
    }
    const row = rowRef.current;
    if (row && row.scrollWidth > row.clientWidth && visibleCount > 1) {
      setVisibleCount(count => count - 1);
    }
  }, [visibleCount, expanded, rowWidth, badges]);

  // Track the row's available width so a resize re-runs the fit measurement.
  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const observer = new ResizeObserver(entries => {
      setRowWidth(entries[0].contentRect.width);
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  if (!badges.length) {
    return null;
  }

  // Collapsed shows the badges that fit + "+N"; expanded shows every badge and
  // the "See less" toggle as one wrapping flow, so "See less" sits at the end of
  // the badges and wraps with them (no separate list below, so toggling doesn't
  // shove the inline badges around).
  const shown = expanded ? badges : badges.slice(0, visibleCount);
  const hiddenCount = badges.length - visibleCount;
  const hasOverflow = hiddenCount > 0;
  const rowClass = expanded
    ? 'badge-overflow-group__row badge-overflow-group__row--expanded'
    : 'badge-overflow-group__row';

  const renderBadge = ({ label, category }, idx) => (
    <div key={`${label}-${idx}`} className={`badge badge--${category}`}>{label}</div>
  );

  return (
    <div className="badge-overflow-group">
      <div className={rowClass} ref={rowRef}>
        {shown.map(renderBadge)}
        {hasOverflow && (
          <button
            type="button"
            className="badge-overflow-group__toggle"
            aria-expanded={expanded}
            aria-label={expanded ? 'Show fewer badges' : `Show ${hiddenCount} more badges`}
            onClick={() => setExpanded(value => !value)}
          >
            {expanded ? 'See Less' : `+${hiddenCount}`}
          </button>
        )}
      </div>
    </div>
  );
}
